import type { TypedSupabaseClient } from '../client';
import { orContainsAnyColumn } from './postgrest-search';
import type {
  ITreasuryRepository,
  BankAccount,
  CashRegisterOption,
  BankAccountWithBank,
  BankAccountType,
  BankAccountFilters,
  CreateBankAccountInput,
  UpdateBankAccountInput,
  TnBank,
  BankTransaction,
  BankTransactionType,
  BankTransactionDirection,
  BankTransactionFilters,
  CreateBankTransactionInput,
  UpdateBankTransactionInput,
} from '../../../interfaces/src';
import { fetchAllRows } from './fetch-all-rows';
import { z } from 'zod/v4';

const drawerAccessSchema = z.array(z.string().uuid()).max(1000).nullable();

export class SupabaseTreasuryRepository implements ITreasuryRepository {
  // Cast to any while Supabase types are not regenerated for new tables (tn_banks, bank_transactions)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- types not yet generated
  private readonly supabase: any;

  constructor(client: TypedSupabaseClient) {
    this.supabase = client;
  }

  // ===== Bank accounts =====

  async listCashRegisterOptions(storeId: string): Promise<CashRegisterOption[]> {
    const { data, error } = await this.supabase.rpc('list_cash_register_options', { p_store_id: storeId });
    if (error) throw error;
    return ((data ?? []) as Array<{ id: string; name: string; store_id: string; is_default_cash_register: boolean; warehouse_id?: string | null }>).map((row) => ({
      id: row.id, name: row.name, storeId: row.store_id, isDefaultCashRegister: row.is_default_cash_register,
      warehouseId: row.warehouse_id ?? null,
    }));
  }

  async storeHasCashRegisters(storeId: string): Promise<boolean> {
    const { data, error } = await this.supabase.rpc('store_has_cash_registers' as never, { p_store_id: storeId } as never);
    if (error) throw error;
    return data === true;
  }

  async listBankAccounts(filters?: BankAccountFilters): Promise<BankAccountWithBank[]> {
    let query = this.supabase
      .from('bank_accounts')
      .select('*, tn_banks(*)');
    if (filters?.storeId !== undefined) {
      query = filters.storeId === null
        ? query.is('store_id', null)
        : query.eq('store_id', filters.storeId);
    }
    if (filters?.accountType) query = query.eq('account_type', filters.accountType);
    if (filters?.activeOnly) query = query.eq('is_active', true);
    const { data, error } = await query.order('name');
    if (error) throw error;
    return (data ?? []).map(mapBankAccountWithBank);
  }

  async getBankAccountById(id: string): Promise<BankAccountWithBank | null> {
    const { data, error } = await this.supabase
      .from('bank_accounts')
      .select('*, tn_banks(*)')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapBankAccountWithBank(data);
  }

  async createBankAccount(input: CreateBankAccountInput): Promise<BankAccountWithBank> {
    const accountType = input.accountType ?? 'bank';
    if (accountType === 'cash' && !input.storeId) {
      throw new Error('cash_register_store_required');
    }
    const insertPayload: Record<string, unknown> = {
      store_id: input.storeId ?? null,
      name: input.name,
      bank_name: input.bankName,
      bank_id: input.bankId ?? null,
      rib: input.rib ?? null,
      address: input.address ?? null,
      current_balance: input.currentBalance ?? 0,
      is_active: input.isActive ?? true,
      account_type: accountType,
      is_default_cash_register: input.isDefaultCashRegister ?? false,
      // CHECK bank_accounts_cash_warehouse_only : un compte bancaire n'a pas d'entrepôt.
      warehouse_id: accountType === 'cash' ? (input.warehouseId ?? null) : null,
      ...(input.allowedUserIds !== undefined ? { allowed_user_ids: drawerAccessSchema.parse(input.allowedUserIds) } : {}),
    };
    const { data, error } = await this.supabase
      .from('bank_accounts')
      .insert(insertPayload)
      .select('*, tn_banks(*)')
      .single();
    if (error) throw error;
    return mapBankAccountWithBank(data);
  }

  async updateBankAccount(id: string, input: UpdateBankAccountInput): Promise<BankAccountWithBank> {
    const mapped: Record<string, unknown> = {};
    if (input.name !== undefined) mapped['name'] = input.name;
    if (input.storeId !== undefined) mapped['store_id'] = input.storeId;
    if (input.bankName !== undefined) mapped['bank_name'] = input.bankName;
    if ('bankId' in input) mapped['bank_id'] = (input as { bankId: string | null }).bankId;
    if (input.rib !== undefined) mapped['rib'] = input.rib;
    if (input.address !== undefined) mapped['address'] = input.address;
    if (input.currentBalance !== undefined) mapped['current_balance'] = input.currentBalance;
    if (input.isActive !== undefined) mapped['is_active'] = input.isActive;
    if (input.accountType !== undefined) mapped['account_type'] = input.accountType;
    if (input.allowedUserIds !== undefined) mapped['allowed_user_ids'] = drawerAccessSchema.parse(input.allowedUserIds);
    if (input.warehouseId !== undefined) mapped['warehouse_id'] = input.warehouseId;
    if (input.isDefaultCashRegister !== undefined) {
      // Une seule caisse par défaut par magasin (index partiel). Désigner une
      // nouvelle caisse doit donc D'ABORD libérer l'ancienne, sinon l'UPDATE
      // se heurte à un 23505 et le réglage semble ne pas se prendre.
      if (input.isDefaultCashRegister) {
        const current = await this.getBankAccountById(id);
        const storeId = input.storeId !== undefined ? input.storeId : current?.storeId;
        if (!storeId) throw new Error('cash_register_store_required');
        const { error: clearError } = await this.supabase
          .from('bank_accounts')
          .update({ is_default_cash_register: false })
          .eq('is_default_cash_register', true)
          .eq('store_id', storeId)
          .neq('id', id);
        if (clearError) throw clearError;
      }
      mapped['is_default_cash_register'] = input.isDefaultCashRegister;
    }

    const { data, error } = await this.supabase
      .from('bank_accounts')
      .update(mapped)
      .eq('id', id)
      .select('*, tn_banks(*)')
      .single();
    if (error) throw error;
    return mapBankAccountWithBank(data);
  }

  async deleteBankAccount(id: string): Promise<void> {
    const { error } = await this.supabase.from('bank_accounts').delete().eq('id', id);
    if (error) throw error;
  }

  // ===== Tunisian banks reference =====

  async listBanks(): Promise<TnBank[]> {
    const { data, error } = await this.supabase
      .from('tn_banks')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
    if (error) throw error;
    return (data ?? []).map(mapTnBank);
  }

  // ===== Bank transactions =====

  async listTransactions(filters?: BankTransactionFilters): Promise<BankTransaction[]> {
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let q = this.supabase.from('bank_transactions').select('*');

      if (filters?.accountId) q = q.eq('bank_account_id', filters.accountId);
      if (filters?.dateFrom) q = q.gte('transaction_date', filters.dateFrom);
      if (filters?.dateTo) q = q.lte('transaction_date', filters.dateTo);
      if (filters?.transactionType)
        q = q.eq('transaction_type', filters.transactionType);
      if (filters?.direction) q = q.eq('direction', filters.direction);
      if (filters?.isReconciled !== undefined)
        q = q.eq('is_reconciled', filters.isReconciled);
      if (filters?.search) {
        q = q.or(
          orContainsAnyColumn(['description', 'reference'], filters.search),
        );
      }

      return q
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to);
    });
    return rows.map(mapTransaction);
  }

  async getTransactionById(id: string): Promise<BankTransaction | null> {
    const { data, error } = await this.supabase
      .from('bank_transactions')
      .select('*')
      .eq('id', id)
      .single();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return mapTransaction(data);
  }

  async createTransaction(input: CreateBankTransactionInput): Promise<BankTransaction> {
    const payload: Record<string, unknown> = {
      bank_account_id:          input.bankAccountId,
      transaction_date:         input.transactionDate,
      value_date:               input.valueDate ?? null,
      direction:                input.direction,
      amount:                   input.amount,
      transaction_type:         input.transactionType,
      reference:                input.reference ?? null,
      description:              input.description ?? null,
      payment_id:               input.paymentId ?? null,
      check_draft_id:           input.checkDraftId ?? null,
      counterparty_account_id:  input.counterpartyAccountId ?? null,
      statement_line_id:        input.statementLineId ?? null,
    };
    const { data, error } = await this.supabase
      .from('bank_transactions')
      .insert(payload)
      .select()
      .single();
    if (error) throw error;
    // Sync the account balance so the treasury pages show the new running total.
    await this._safeRecalc(input.bankAccountId);
    return mapTransaction(data);
  }

  async updateTransaction(id: string, input: UpdateBankTransactionInput): Promise<BankTransaction> {
    const mapped: Record<string, unknown> = {};
    if (input.transactionDate !== undefined)       mapped['transaction_date'] = input.transactionDate;
    if (input.valueDate !== undefined)             mapped['value_date'] = input.valueDate;
    if (input.direction !== undefined)             mapped['direction'] = input.direction;
    if (input.amount !== undefined)                mapped['amount'] = input.amount;
    if (input.transactionType !== undefined)       mapped['transaction_type'] = input.transactionType;
    if (input.reference !== undefined)             mapped['reference'] = input.reference;
    if (input.description !== undefined)           mapped['description'] = input.description;
    if (input.paymentId !== undefined)             mapped['payment_id'] = input.paymentId;
    if (input.checkDraftId !== undefined)          mapped['check_draft_id'] = input.checkDraftId;
    if (input.counterpartyAccountId !== undefined) mapped['counterparty_account_id'] = input.counterpartyAccountId;
    if (input.statementLineId !== undefined)       mapped['statement_line_id'] = input.statementLineId;

    const { data, error } = await this.supabase
      .from('bank_transactions')
      .update(mapped)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    const accountId = (data as { bank_account_id?: string } | null)?.bank_account_id;
    if (accountId) await this._safeRecalc(accountId);
    return mapTransaction(data);
  }

  async deleteTransaction(id: string): Promise<void> {
    // Remember the account before deletion so we can refresh its balance after.
    const { data: row } = await this.supabase
      .from('bank_transactions')
      .select('bank_account_id')
      .eq('id', id)
      .single();
    const accountId = (row as { bank_account_id?: string } | null)?.bank_account_id;
    const { error } = await this.supabase.from('bank_transactions').delete().eq('id', id);
    if (error) throw error;
    if (accountId) await this._safeRecalc(accountId);
  }

  async reconcileTransactions(ids: string[], reconciled: boolean): Promise<void> {
    const payload: Record<string, unknown> = {
      is_reconciled: reconciled,
      reconciled_at: reconciled ? new Date().toISOString() : null,
    };
    const { error } = await this.supabase
      .from('bank_transactions')
      .update(payload)
      .in('id', ids);
    if (error) throw error;
  }

  async recalculateBalance(accountId: string): Promise<number> {
    const { data, error } = await this.supabase
      .rpc('recalculate_bank_account_balance', { p_account_id: accountId });
    if (error) throw error;
    return (data as number) ?? 0;
  }

  /**
   * Best-effort balance recalc — never throws, so the calling transaction flow
   * (insert/update/delete) still succeeds even if the RPC isn't deployed yet on
   * the tenant DB (migration not applied). A stale balance is recoverable via
   * the "Recalculer le solde" button in the treasury UI.
   *
   * Returns `true` when the balance was recalculated, `false` when the recalc
   * is *pending* (RPC errored / not deployed). The flag lets callers surface a
   * "balance may be stale" signal instead of failing silently; we also emit a
   * tagged warning so the pending state is observable in logs.
   */
  private async _safeRecalc(accountId: string): Promise<boolean> {
    const { error } = await this.supabase
      .rpc('recalculate_bank_account_balance', { p_account_id: accountId });
    if (error) {
      console.warn(
        `[treasury-repository] balance recalc PENDING for account ${accountId} ` +
          `(write succeeded, balance may be stale until manual "Recalculer le solde"):`,
        error.message,
      );
      return false;
    }
    return true;
  }
}

// ===== Mappers =====

function mapTnBank(row: Record<string, unknown>): TnBank {
  return {
    id:            row['id'] as string,
    code:          row['code'] as string,
    shortName:     row['short_name'] as string,
    fullName:      row['full_name'] as string,
    fullNameAr:    (row['full_name_ar'] as string) ?? null,
    swiftBic:      (row['swift_bic'] as string) ?? null,
    website:       (row['website'] as string) ?? null,
    isIslamic:     (row['is_islamic'] as boolean) ?? false,
    color:         (row['color'] as string) ?? null,
    logoUrl:       (row['logo_url'] as string) ?? null,
    displayOrder:  (row['display_order'] as number) ?? 100,
    isActive:      (row['is_active'] as boolean) ?? true,
    createdAt:     row['created_at'] as string,
  };
}

function mapBankAccount(row: Record<string, unknown>): BankAccount {
  return {
    id:             row['id'] as string,
    storeId:        (row['store_id'] as string | null) ?? null,
    name:           row['name'] as string,
    bankName:       (row['bank_name'] as string) ?? '',
    bankId:         (row['bank_id'] as string) ?? null,
    rib:            (row['rib'] as string) ?? null,
    address:        (row['address'] as string) ?? null,
    currentBalance: (row['current_balance'] as number) ?? 0,
    isActive:       (row['is_active'] as boolean) ?? true,
    accountType:    ((row['account_type'] as BankAccountType) ?? 'bank'),
    isDefaultCashRegister: (row['is_default_cash_register'] as boolean) ?? false,
    warehouseId:    (row['warehouse_id'] as string | null) ?? null,
    allowedUserIds: (row['allowed_user_ids'] as string[] | null) ?? null,
    createdAt:      row['created_at'] as string,
  };
}

function mapBankAccountWithBank(row: Record<string, unknown>): BankAccountWithBank {
  const base = mapBankAccount(row);
  const bankRow = row['tn_banks'] as Record<string, unknown> | null | undefined;
  return {
    ...base,
    bank: bankRow ? mapTnBank(bankRow) : null,
  };
}

function mapTransaction(row: Record<string, unknown>): BankTransaction {
  return {
    id:                     row['id'] as string,
    bankAccountId:          row['bank_account_id'] as string,
    transactionDate:        row['transaction_date'] as string,
    valueDate:              (row['value_date'] as string) ?? null,
    direction:              row['direction'] as BankTransactionDirection,
    amount:                 row['amount'] as number,
    transactionType:        row['transaction_type'] as BankTransactionType,
    reference:              (row['reference'] as string) ?? null,
    description:            (row['description'] as string) ?? null,
    paymentId:              (row['payment_id'] as string) ?? null,
    checkDraftId:           (row['check_draft_id'] as string) ?? null,
    counterpartyAccountId:  (row['counterparty_account_id'] as string) ?? null,
    isReconciled:           (row['is_reconciled'] as boolean) ?? false,
    reconciledAt:           (row['reconciled_at'] as string) ?? null,
    reconciledBy:           (row['reconciled_by'] as string) ?? null,
    statementLineId:        (row['statement_line_id'] as string) ?? null,
    createdBy:              (row['created_by'] as string) ?? null,
    createdAt:              row['created_at'] as string,
    updatedAt:              row['updated_at'] as string,
  };
}
